/**
 * Export to workpapers.
 *   T6-H9  in custody: an info state, Export, and (leads) "Acknowledge custody".
 *   T6-H11 the manifest's seal is a "Sealed" badge; who seals it is behind "Verify".
 *   T6-H15 Download goes through fetch: a stale sign-in steps up; a missing or
 *          tampered stored export shows inline with "Export again".
 */
import { useState } from 'react';
import { Alert, EmptyState } from '@aigovops/design-system';
import { downloadExport, firmApi, type FirmExport } from '../../lib/firm-api';
import { fmtDate, plural } from '../../lib/format';
import { stepUpUrlOf } from '../../lib/firm-step-up';
import { GuardedButton, SealBadge, TabHeading, errCode, errText, useAct, useFirm, useLoad, Loading } from './firm-kit';

const kb = (n: number) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function ExportTab() {
  const { slug, eng, o, lead, announce, requireStepUp } = useFirm();
  const l = useLoad(() => firmApi.exports(slug, eng), [slug, eng]);
  const act = useAct(l.reload);
  const [dl, setDl] = useState<{ id: string; text: string; code: string | null } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const custody = o.phase === 'custody';

  const exportNow = () => act.run(async () => {
    const r = await firmApi.createExport(slug, eng);
    announce(`Export ready · ${plural(r.export.file_count, 'file')}`);
  });
  const download = async (x: FirmExport) => {
    if (busyId) return;
    setBusyId(x.export_id);
    setDl(null);
    try {
      const name = await downloadExport(slug, eng, x.export_id);
      announce(`Downloaded ${name}`);
    } catch (e) {
      const url = stepUpUrlOf(e);
      if (url) requireStepUp(url);
      else setDl({ id: x.export_id, text: errText(e), code: errCode(e) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="gp-firm-stack">
      <TabHeading id="export-h">Export</TabHeading>
      {custody ? (
        <EmptyState title="Custody · partner and manager only"
          description={o.engagement.revoked_at ? 'The client ended access. Your working papers stay readable and exportable for the retention period.' : 'The assembly window has closed. Your working papers stay readable and exportable for the retention period.'}
          action={(
            <span className="gp-firm-actions">
              <GuardedButton variant="primary" busy={act.busy} onClick={() => void exportNow()}>Export working papers</GuardedButton>
              {lead ? <GuardedButton busy={act.busy} onClick={() => void act.call('acknowledgeCustody', [slug, eng], 'Custody acknowledged')}>Acknowledge custody</GuardedButton> : null}
            </span>
          )} />
      ) : (
        <div className="gp-firm-actions">
          <GuardedButton variant="primary" busy={act.busy} onClick={() => void exportNow()}>Export working papers</GuardedButton>
          <span className="gp-firm-muted">Indexed zip as of now, with a sealed manifest.</span>
        </div>
      )}
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Loading l={l}>{(d) => d.exports.length === 0 ? null : (
        <ul className="gp-firm-list" aria-label="Past exports">
          {d.exports.map((x) => (
            <li key={x.export_id} className="gp-firm-row">
              <div className="gp-firm-row-head">
                <h3 className="gp-firm-row-title">As of {fmtDate(x.as_of)} · {plural(x.file_count, 'file')} · {kb(x.size_bytes)}</h3>
                <GuardedButton compact busy={busyId === x.export_id} aria-label={`Download the export as of ${fmtDate(x.as_of)}`} onClick={() => void download(x)}>Download</GuardedButton>
              </div>
              <div className="gp-firm-actions">
                <span className="gp-firm-muted">{x.includes_client_tier ? 'Firm and client tier' : 'Firm tier only · client access ended'}</span>
                <SealBadge label={x.signer_label} />
              </div>
              {dl?.id === x.export_id ? (
                <Alert variant="error" title={dl.code === 'export_tampered' ? 'This export failed its integrity check' : dl.code === 'export_missing' ? 'This export’s file is missing' : 'Download failed'}>
                  {dl.text} <GuardedButton compact busy={act.busy} onClick={() => void exportNow()}>Export again</GuardedButton>
                </Alert>
              ) : null}
            </li>
          ))}
        </ul>
      )}</Loading>
    </div>
  );
}
